import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { ReactComponent as BagFood } from "../../../assets/svg/icons/bagFood.svg";
import { FilterDateDashboard } from "../../../components/Dashboard/ViewDatas/FilterDate";
import { useFilter } from "../../../hooks/useFilter";
import Constants from "../../../constants/Index";
import { DateModal } from "../../../components/Modal/DateFilter";
import { Dropdown } from "../../../components/EditOnboarding/components/Select";
import { useForm, useWatch } from "react-hook-form";
import axios from "../../../services/axios";
import { IFarm, IFarms } from "../../../@types/API/IFarm";
import { IOptionProps } from "../../../@types/reactSelect/IOption";
import { farmService } from "../../../services/farmService";
import { getFarmsAsOptions } from "../../../utils/farms/farmsAsOptions";
import { areaService } from "../../../services/areaService";
import { getAreasAsOptions } from "../../../utils/farms/areasAsOptions";
import { IAreas } from "../../../@types/API/IArea";
import { ButtonWithChildren } from "../../../components/Buttons/ButtonWithChildren/Index";
import { analyticsService } from "../../../services/analyticsService";
import { exportDailySnapshotToExcel, exportDailySnapshotByBatchToExcel } from "../../../utils/excel/exportDailySnapshot";
import { format } from "date-fns";

export default function ExportDashboard() {
    const {
        setDashboard,
        setSideBarOptionSelected,
        onBoardingDate,
        rangeDays,
        setRangeDays,
        setSelectRangeDate
    } = useFilter();

    const {
        control,
        watch,
        setValue
    } = useForm();

    const selectedFarmsWatch = useWatch({
        control,
        name: "farms"
    });

    const normalizeAreasForDropdown = useCallback((areasList: IOptionProps[]): IOptionProps[] => {
        return areasList.map((area: any) => ({
            ...area,
            value: String(area.value),
        }));
    }, []);

    useEffect(() => {
        setDashboard("EXPORT")
        document.title = Constants.TITLE_EXPORT;
        setSideBarOptionSelected("DASHBOARD")
        setSelectRangeDate(2)

        const now = new Date();
        now.setDate(now.getDate() - 1);

        setRangeDays({
            from: onBoardingDate,
            to: now
        })
    }, [, onBoardingDate]);

    const [farms, setFarms] = useState<IOptionProps[]>();
    const [areas, setAreas] = useState<IOptionProps[]>();
    const [isLoading, setIsLoading] = useState(false);
    const previousFarmsRef = useRef<any[]>([]);

    useMemo(() => {
        farmService.getFarmsByUser().then(response => {
            const farmsOptions = getFarmsAsOptions(response as unknown as IFarms);
            setFarms(farmsOptions);
        })
    }, []);

    useMemo(() => {
        areaService.getAreasByUser().then(response => {
            const areasOptions = getAreasAsOptions(response as unknown as IAreas);
            setAreas(areasOptions);
        })
    }, []);

    // Monitorar mudanças nas fazendas selecionadas
    useEffect(() => {
        const selectedFarms = selectedFarmsWatch || [];
        const selectedAreas = watch("areas") || [];
        const previousFarms = previousFarmsRef.current;
        
        if (!areas || areas.length === 0) {
            previousFarmsRef.current = selectedFarms;
            return;
        }
        if (!farms || farms.length === 0) {
            previousFarmsRef.current = selectedFarms;
            return;
        }
        
        const selectedFarmIds = selectedFarms.map((farm: any) => Number(farm.value));
        const previousFarmIds = previousFarms.map((farm: any) => Number(farm.value));
        
        // Detectar se todas as fazendas foram selecionadas ou desselecionadas
        const allFarmsSelected = selectedFarms.length === farms.length;
        const allFarmsDeselected = selectedFarms.length === 0;
        const previousAllFarmsSelected = previousFarms.length === farms.length;
        
        if (allFarmsSelected && !previousAllFarmsSelected) {
            // Selecionar todas as áreas disponíveis, normalizando valores para String
            const normalizedAreas = normalizeAreasForDropdown(areas);
            setValue("areas", normalizedAreas);
            previousFarmsRef.current = selectedFarms;
            return;
        }
        
        if (allFarmsDeselected && previousFarms.length > 0) {
            // Remover todas as áreas selecionadas
            setValue("areas", []);
            previousFarmsRef.current = selectedFarms;
            return;
        }
        
        const newFarmIds = selectedFarmIds.filter((id: number) => !previousFarmIds.includes(id));
        
        if (newFarmIds.length > 0) {
            const newAreas = areas.filter((area: any) => 
                newFarmIds.includes(area.info.farm.id)
            );
            
            const existingAreaValues = selectedAreas.map((area: any) => String(area.value));
            const areasToAdd = newAreas.filter((area: any) => 
                !existingAreaValues.includes(String(area.value))
            );
        }
        
        if (selectedAreas.length > 0 && selectedFarms.length > 0) {
            const validAreas = selectedAreas.filter((area: any) => 
                selectedFarmIds.includes(area.info.farm.id)
            );
            
            // Se algum retiro foi removido, atualizar o campo, normalizando valores
            if (validAreas.length !== selectedAreas.length) {
                const normalizedValidAreas = normalizeAreasForDropdown(validAreas);
                setValue("areas", normalizedValidAreas);
                previousFarmsRef.current = selectedFarms;
                return;
            }
        } else if (selectedFarms.length === 0 && selectedAreas.length > 0) {
            // Se não há fazendas selecionadas, remover todas as áreas
            setValue("areas", []);
            previousFarmsRef.current = selectedFarms;
            return;
        }
        
        previousFarmsRef.current = selectedFarms;
    }, [selectedFarmsWatch, setValue, areas, farms, watch, normalizeAreasForDropdown]);

    const handleExport = async (isByBatch: boolean) => {
        try {
            // Validações
            const selectedFarms = watch("farms");
            const selectedAreas = watch("areas");

            if (!rangeDays?.from || !rangeDays?.to) {
                alert("Por favor, selecione um período de datas");
                return;
            }

            setIsLoading(true);

            // Preparar parâmetros
            const farmId = selectedFarms.map((farm: any) => Number(farm.value));
            const areaIds = selectedAreas.map((area: any) => Number(area.value));
            const startDate = format(new Date(rangeDays.from), 'yyyy-MM-dd');
            const endDate = format(new Date(rangeDays.to), 'yyyy-MM-dd');

            // Fazer requisição
            const data = await analyticsService.getDailySnapshot(farmId, areaIds, startDate, endDate, isByBatch);

            // Exportar para Excel
            const filename = isByBatch 
                ? `daily-snapshot-by-batch-${startDate}-to-${endDate}.xlsx`
                : `daily-snapshot-by-microarea-${startDate}-to-${endDate}.xlsx`;
            
            if (isByBatch) {
                exportDailySnapshotByBatchToExcel(data, filename);
            } else {
                exportDailySnapshotToExcel(data, filename);
            }
        } catch (error) {
            console.error('Erro ao exportar dados:', error);
            alert("Erro ao gerar arquivo Excel. Tente novamente.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportByMicroArea = () => handleExport(false);
    const handleExportByBatch = () => handleExport(true);

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() - 1);

    return (
        <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
            <DateModal maxDate={maxDate}/>
            
            <form className="dashboardPastureManagement">
                <div className="cattleMap_page">
                    <div className="cattleMap_header">
                        <BagFood height={72} />
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                        }}>
                            <h2 className="cattleMap_title">
                                Exportar
                            </h2>
                            <h3 className="cattleMap_subtitle">
                                Exporte os dados para Excel
                            </h3>
                        </div>
                    </div>

                    <div>
                        <FilterDateDashboard exportPDF={false} />
                        <div style={{ width: 400, display: "flex", flexDirection: "column" }}>
                            <Dropdown
                                control={control}
                                label={"Seleção de Fazendas"}
                                name={"farms"}
                                placeholderText={"Selecione..."}
                                options={farms}
                                isMulti
                                selectAll={true}
                                selectAllLabel="Selecionar Agropecuária"
                                noOptionsMessage={() => "Nenhuma opção disponível"}
                            />

                            <Dropdown
                                control={control}
                                label={"Seleção de Retiros"}
                                name={"areas"}
                                placeholderText={"Selecione..."}
                                options={areas?.filter((area) => watch("farms")?.map((farm: any) => Number(farm.value)).includes(area.info.farm.id))}
                                isMulti
                                selectAll={true}
                                selectAllLabel="Selecionar Todos"
                                hideSelectedOptions={true}
                                noOptionsMessage={() => "Nenhuma opção disponível"}
                            />

                            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                                <ButtonWithChildren
                                    onClick={handleExportByMicroArea}
                                    theme={isLoading ? "disabled" : undefined}
                                    children={isLoading ? "Gerando..." : "Por Pasto"}
                                />
                                <ButtonWithChildren
                                    onClick={handleExportByBatch}
                                    theme={isLoading ? "disabled" : undefined}
                                    children={isLoading ? "Gerando..." : "Por Lote"}
                                />
                            </div>
                        </div>

                    </div>
                </div>
            </form>
        </div>
    )
}